/**
 * GSM-7 encoding arithmetic and transliteration (doc 21 D4).
 *
 * The whole reason this file exists: Polish diacritics are NOT in the GSM-7
 * default alphabet, so a single `ó` switches a message to UCS-2 and cuts the
 * segment from 160 characters to 70. A normal booking confirmation goes from
 * one segment to two or three, and the bill doubles or triples — a bigger
 * effect than which vendor carries the message.
 *
 * German and French are deliberately NOT flattened: `ä ö ü ß É è é ù à ò Ç Æ`
 * are all *in* GSM-7, so "Termin bestätigt" and "rendez-vous confirmé" cost
 * exactly what "Termin bestatigt" would. Only what GSM-7 genuinely cannot
 * carry is transliterated.
 */
/** The GSM 03.38 default alphabet, in code-point order. `\f` sits in the
 * extension table, not here. */
const GSM7_BASIC = '@£$¥èéùìòÇ\nØø\rÅå' +
    'Δ_ΦΓΛΩΠΨΣΘΞ\x1bÆæßÉ' +
    " !\"#¤%&'()*+,-./" +
    '0123456789:;<=>?' +
    '¡ABCDEFGHIJKLMNO' +
    'PQRSTUVWXYZÄÖÑÜ§' +
    '¿abcdefghijklmno' +
    'pqrstuvwxyzäöñüà';
/** Reachable only via an ESC prefix, so each of these costs **two** characters. */
const GSM7_EXTENDED = '\f^{}\\[~]|€';
const BASIC = new Set(GSM7_BASIC);
const EXTENDED = new Set(GSM7_EXTENDED);
/** Characters we can carry losslessly, one way or another. */
export function isGsm7(text) {
    return [...text].every((c) => BASIC.has(c) || EXTENDED.has(c));
}
/**
 * What GSM-7 cannot encode, mapped to a form a human still reads correctly.
 *
 * `Strzyzenie` is unambiguously readable; `Strzy?enie` is not — which is why
 * this is a deliberate table rather than a lossy strip, and why we do not
 * delegate it to a provider's "automatic normalisation" (a per-vendor
 * behaviour we would be trusting blind).
 */
const TRANSLITERATE = {
    // Polish
    ą: 'a', Ą: 'A', ć: 'c', Ć: 'C', ę: 'e', Ę: 'E', ł: 'l', Ł: 'L',
    ń: 'n', Ń: 'N', ó: 'o', Ó: 'O', ś: 's', Ś: 'S', ź: 'z', Ź: 'Z', ż: 'z', Ż: 'Z',
    // Latin letters GSM-7 lacks even though a neighbouring case/accent exists.
    // `ç` is the sharp one: GSM-7 has Ç but not ç.
    ç: 'c', â: 'a', ê: 'e', î: 'i', ô: 'o', û: 'u', ë: 'e', ï: 'i', ÿ: 'y',
    À: 'A', Â: 'A', È: 'E', Ê: 'E', Ë: 'E', Î: 'I', Ï: 'I', Ô: 'O',
    Ù: 'U', Û: 'U', œ: 'oe', Œ: 'OE',
    á: 'a', í: 'i', ú: 'u', ý: 'y', Á: 'A', Í: 'I', Ú: 'U',
    ã: 'a', õ: 'o', Ã: 'A', Õ: 'O',
    č: 'c', š: 's', ž: 'z', Č: 'C', Š: 'S', Ž: 'Z', ě: 'e', ř: 'r', ů: 'u', ď: 'd', ť: 't', ň: 'n',
    // Typography that word processors and translators smuggle in.
    '‘': "'", '’': "'", '‚': "'", '“': '"', '”': '"', '„': '"',
    '–': '-', '—': '-', '−': '-', ' ': ' ', ' ': ' ', '…': '...',
    '«': '"', '»': '"', '•': '-', '·': '.',
};
/**
 * Force `text` into the GSM-7 alphabet: transliterate what has a readable
 * equivalent, drop what does not. Total — the result always satisfies
 * {@link isGsm7}, so a template can never silently cost double.
 */
export function toGsm7(text) {
    let out = '';
    for (const ch of text) {
        if (BASIC.has(ch) || EXTENDED.has(ch)) {
            out += ch;
            continue;
        }
        const mapped = TRANSLITERATE[ch];
        if (mapped !== undefined) {
            out += mapped;
            continue;
        }
        // Last resort: strip combining marks (é decomposed, Ukrainian й, …) and
        // retry once. Anything still unencodable is dropped rather than guessed.
        const decomposed = ch.normalize('NFD').replace(/\p{M}+/gu, '');
        for (const d of decomposed) {
            if (BASIC.has(d) || EXTENDED.has(d))
                out += d;
            else if (TRANSLITERATE[d] !== undefined)
                out += TRANSLITERATE[d];
        }
    }
    return out;
}
const GSM7_SINGLE = 160;
const GSM7_MULTI = 153;
const UCS2_SINGLE = 70;
const UCS2_MULTI = 67;
/**
 * What a message actually costs. Segment counts are what the vendor bills, so
 * this is the number the ledger records and the budget test asserts on.
 */
export function smsSize(text) {
    if (isGsm7(text)) {
        let units = 0;
        for (const ch of text)
            units += EXTENDED.has(ch) ? 2 : 1;
        const segments = units <= GSM7_SINGLE ? 1 : Math.ceil(units / GSM7_MULTI);
        return { encoding: 'gsm7', units, segments: Math.max(1, segments) };
    }
    // UTF-16 code units is exactly what UCS-2 counts.
    const units = text.length;
    const segments = units <= UCS2_SINGLE ? 1 : Math.ceil(units / UCS2_MULTI);
    return { encoding: 'ucs2', units, segments: Math.max(1, segments) };
}
/** Truncate to `max` GSM-7 units, ending on a word boundary when one is close
 * enough that cutting there does not lose half the meaning. */
export function clampText(text, max) {
    const t = text.trim();
    if (t.length <= max)
        return t;
    const hard = t.slice(0, max);
    const space = hard.lastIndexOf(' ');
    return (space >= max - 8 ? hard.slice(0, space) : hard).trimEnd();
}
//# sourceMappingURL=gsm7.js.map